import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';

/**
 * InfoTooltip — small (ⓘ) icon that reveals an explanatory tooltip on
 * hover (desktop) or tap (mobile). The tooltip uses a solid dark navy
 * background with white text so it stays perfectly readable on any sky
 * background (day, sunset, night).
 *
 * Props:
 *   text     : string  — tooltip content (already localised)
 *   testId   : string  — data-testid for the trigger
 *   side     : 'top' | 'bottom' (default: 'top')
 *   size     : px size of the icon (default 14)
 *   dim      : if true, render the icon at low opacity until hovered
 */
const InfoTooltip = ({ text, testId, side = 'top', size = 14, dim = true }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside tap (mobile)
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  const tooltipPosition = side === 'top'
    ? { bottom: 'calc(100% + 8px)' }
    : { top: 'calc(100% + 8px)' };

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex items-center"
      style={{ verticalAlign: 'middle' }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="inline-flex items-center justify-center transition-opacity relative"
        style={{
          width: size + 6,
          height: size + 6,
          borderRadius: '50%',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'help',
          opacity: dim && !open ? 0.55 : 1,
          color: '#E2E8F0',
          // Larger touch target for mobile (invisible, doesn't affect layout)
          WebkitTapHighlightColor: 'rgba(255,255,255,0.15)',
        }}
        aria-label="More information"
        data-testid={testId}
      >
        <Info size={size} strokeWidth={2} />
        {/* Invisible tap-area expander — meets the 44×44 px touch-target guideline */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: '-12px',
            content: '""'
          }}
        />
      </button>

      {open && (
        <span
          role="tooltip"
          className="absolute z-50"
          style={{
            ...tooltipPosition,
            left: '50%',
            transform: 'translateX(-50%)',
            minWidth: 220,
            maxWidth: 320,
            padding: '10px 14px',
            background: '#0B132B',
            color: '#FFFFFF',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4)',
            fontSize: 'clamp(0.8125rem, 0.95vw, 1rem)',
            lineHeight: 1.55,
            textAlign: 'start',
            whiteSpace: 'pre-line',
            fontWeight: 400,
            letterSpacing: 0,
            pointerEvents: 'none'
          }}
          data-testid={testId ? `${testId}-content` : undefined}
        >
          {text}

          {/* Arrow */}
          <span
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 10,
              height: 10,
              background: '#0B132B',
              border: '1px solid rgba(255,255,255,0.18)',
              borderTop: side === 'top' ? 'none' : '1px solid rgba(255,255,255,0.18)',
              borderLeft: side === 'top' ? 'none' : '1px solid rgba(255,255,255,0.18)',
              borderBottom: side === 'top' ? '1px solid rgba(255,255,255,0.18)' : 'none',
              borderRight: side === 'top' ? '1px solid rgba(255,255,255,0.18)' : 'none',
              [side === 'top' ? 'bottom' : 'top']: -5
            }}
          />
        </span>
      )}
    </span>
  );
};

export default InfoTooltip;
